import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimeField from 'packages/novous_web/src/common/components/TimeField';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';

import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import AlertToast from 'packages/novous_web/src/common/components/Alert';

interface DateTimeModalProps {
  open: boolean;
  tripId: string;
  onClose: () => void;
}

// DateField Component (moved inside)
interface DateFieldProps {
  label: string;
  name: string;
  value: string | null;
  onChange: (value: string | null) => void;
  isDisabled?: boolean;
}

const DateField: React.FC<DateFieldProps> = ({
  label,
  name,
  value,
  onChange,
  isDisabled = false,
}) => {
  return (
    <div className="flex flex-col w-full">
      <Typography variant="body2" fontWeight={600} color="textPrimary">
        {label}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value ? dayjs(value, 'DD/MM/YYYY') : null}
          onChange={(newValue) =>
            onChange(newValue ? dayjs(newValue).format('DD/MM/YYYY') : null)
          }
          format="DD/MM/YYYY"
          disabled={isDisabled}
          slotProps={{
            textField: { fullWidth: true, variant: 'outlined' },
          }}
          sx={(theme) => ({
            width: '100%',
            marginTop: '8px',
            '& .MuiOutlinedInput-root': {
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 400,
              borderRadius: '8px',
              borderWidth: '1px',
              borderColor: theme.palette.action.selected,
            },
            '& .MuiInputBase-input': {
              padding: '0px',
              borderWidth: '0px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSvgIcon-root': {
              color: theme.palette.primary.main,
            },
          })}
          className={`mt-2 w-full ${
            isDisabled ? 'bg-gray-200 cursor-not-allowed' : ''
          }`}
        />
      </LocalizationProvider>
    </div>
  );
};

// DateTimeModal Component
const DateTimeModal: React.FC<DateTimeModalProps> = ({
  open,
  tripId,
  onClose,
}) => {
  const { user } = useAuth();
  const [toast, setToast] = useState({ open: false, message: "", type: "error" });

  
  const [formData, setFormData] = useState({
    departureDate: '',
    departureTime: '',
    arrivalDate: '',
    arrivalTime: '',
  });

  const {
    data: cloneTripData,
    error: cloneTripError,
    loading: cloneTripLoading,
    fetchData: fetchCloneTripData,
  } = useAxios<{data: any, message: string}>(`${API_ENDPOINTS.trip.CLONE_TRIP}`, 'POST');

  useEffect(()=>{
    if(cloneTripData && cloneTripData.message == 'Trip clone and  Created Successfully'){
      setToast({open: true, message: 'Trip cloned' , type:'success'})
      setTimeout(()=>{
        onClose();
      },3000)
    }
  },[cloneTripData])

  const handleDateChange = (
    field: 'departureDate' | 'arrivalDate',
    date: string | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date || '',
    }));
  };

  const handleTimeChange = (
    field: 'departureTime' | 'arrivalTime',
    time: string | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: time || '',
    }));
  };

  const formatDate = (date: string | null) => {
    if (!date) return '';
    const parsedDate = dayjs(date, 'DD/MM/YYYY').toDate();
    return parsedDate.toISOString().split('T')[0]; // YYYY-MM-DD
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const timeParts = time.match(/\d+/g);
    if (!timeParts || timeParts.length < 2) return '';
    const [hours, minutes] = timeParts;
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`; // HH:MM
  };

  const submitCloneTrip = () => {
    const payload = {
      departureDate: formatDate(formData.departureDate),
      departureTime: formatTime(formData.departureTime),
      arrivalDate: formatDate(formData.arrivalDate),
      arrivalTime: formatTime(formData.arrivalTime),
      fleetId: user?.customer_id,
      tripId,
    };

    console.log('Submitting clone trip payload:', payload);
    fetchCloneTripData(payload);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitCloneTrip();
  };

  const handleCloneClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    submitCloneTrip();
  };

  const isFormValid =
    formData.departureDate &&
    formData.departureTime &&
    formData.arrivalDate &&
    formData.arrivalTime;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">Select Date & Time</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Departure Fields */}
          <div className="flex gap-4 flex-wrap mb-4">
            <div className="flex-1" style={{ flex: 1, minWidth: '45%' }}>
              <DateField
                label="Departure Date"
                name="departureDate"
                value={formData.departureDate}
                onChange={(date) => handleDateChange('departureDate', date)}
              />
            </div>
            <div className="flex-1" style={{ flex: 1, minWidth: '45%' }}>
              <TimeField
                label="Departure Time"
                name="departureTime"
                value={formData.departureTime}
                onChange={(time) => handleTimeChange('departureTime', time)}
              />
            </div>
          </div>

          {/* Arrival Fields */}
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1" style={{ flex: 1, minWidth: '45%' }}>
              <DateField
                label="Arrival Date"
                name="arrivalDate"
                value={formData.arrivalDate}
                onChange={(date) => handleDateChange('arrivalDate', date)}
              />
            </div>
            <div className="flex-1" style={{ flex: 1, minWidth: '45%' }}>
              <TimeField
                label="Arrival Time"
                name="arrivalTime"
                value={formData.arrivalTime}
                onChange={(time) => handleTimeChange('arrivalTime', time)}
              />
            </div>
          </div>

          {/* Footer Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 4,
              gap: 2,
            }}
          >
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline={false}
              padding="0px"
              size={16}
              variant="outlined"
              onClick={onClose}
            />
            <CustomIconButton
              text="Clone Trip"
              outline={false}
              padding="0px"
              isDisabled={!isFormValid}
              size={16}
              variant="contained"
              onClick={handleCloneClick}
            />
          </Box>
        </Box>
      </Modal>
      <AlertToast open={toast.open} message={toast.message} onClose={() => setToast({ ...toast, open: false })} type={toast.type as any} />

    </>
  );
};

export default DateTimeModal;
